import IconFaceMask from '@picsart/icons/IconFaceMask';
import IconBlankWrite from '@picsart/icons/IconBlankWrite';
import IconRefresh from '@picsart/icons/IconRefresh';
import IconEmail from '@picsart/icons/IconEmail';
import IconHashtag from '@picsart/icons/IconHashtag';
import IconEmbedCode from '@picsart/icons/IconEmbedCode';
import IconLearn from '@picsart/icons/IconLearn';
import IconEmailHeader from '@picsart/icons/IconEmailHeader';
import IconSketch from '@picsart/icons/IconSketch';
import IconDetails from '@picsart/icons/IconDetails';
import IconJob from '@picsart/icons/IconJob';
import IconPromote from '@picsart/icons/IconPromote';
import IconProfileOutline from '@picsart/icons/IconProfileOutline';
import IconTemplate from '@picsart/icons/IconTemplate';
import IconBookmark from '@picsart/icons/IconBookmark';
import IconCallout from '@picsart/icons/IconCallout';
import IconAssignmentTurnedIn from '@picsart/icons/IconAssignmentTurnedIn';
import IconPoster from '@picsart/icons/IconPoster';
import IconSubtitle from '@picsart/icons/IconSubtitle';
import IconFeedback from '@picsart/icons/IconFeedback';
import IconTemplates from '@picsart/icons/IconTemplates';
import IconDevelop from '@picsart/icons/IconDevelop';
import IconSpacingVertical from '@picsart/icons/IconSpacingVertical';
import IconTextToVideo from '@picsart/icons/IconTextToVideo';
import IconSubtitleSquare from '@picsart/icons/IconSubtitleSquare';
import IconFold from '@picsart/icons/IconFold';
import IconUnfold from '@picsart/icons/IconUnfold';

import {
  AllCategories,
  PromptPartType,
  IValues,
  MenuCategoryType,
  MenuCategory,
} from '../utils/common/types';

export const GPT_VERSION = 'gpt-3.5-turbo';
export const GPT_4_3K_VERSION = 'gpt-4-32k';

export const AD_WRITER_CATEGORY = 0;
export const SOCIAL_MEDIA_BIO_CATEGORY = 1;
export const REPHRASER_CATEGORY = 2;
export const MARKETING_SLOGAN_CATEGORY = 3;
export const BUSINESS_NAME_CATEGORY = 4;
export const INSTAGRAM_HASHTAG_CATEGORY = 5;
export const PRODUCT_DESCRIPTION_CATEGORY = 6;
export const INSTAGRAM_CAPTION_CATEGORY = 8;
export const PAGE_OUTLINE_CATEGORY = 9;
export const META_DATA_MAKER_CATEGORY = 11;
export const EMAIL_WRITER_CATEGORY = 13;
export const SUMMARIZER_CATEGORY = 15;
export const IDEA_GENERATOR_CATEGORY = 17;
export const ARTICLE_WRITER_CATEGORY = 18;
export const STORY_GENERATOR_CATEGORY = 19;
export const TITLE_GENERATOR_CATEGORY = 20;
export const TRANSLATOR_CATEGORY = 21;
export const GRAMMAR_CHECKER_CATEGORY = 22;
export const ESSAY_WRITER_CATEGORY = 23;
export const BLOG_IDEA_GENERATOR_CATEGORY = 24;
export const BOOK_IDEA_GENERATOR_CATEGORY = 25;
export const BUSINESS_IDEA_GENERATOR_CATEGORY = 26;
export const CONTENT_IDEA_GENERATOR_CATEGORY = 27;
export const DRAWING_IDEA_GENERATOR_CATEGORY = 28;
export const GIFT_IDEA_GENERATOR_CATEGORY = 29;
export const PAINTING_IDEA_GENERATOR_CATEGORY = 30;
export const SIDE_HUSTLE_IDEA_GENERATOR_CATEGORY = 31;
export const STARTUP_IDEA_GENERATOR_CATEGORY = 32;
export const STORY_IDEA_GENERATOR_CATEGORY = 33;
export const CREATE_CATEGORY = 34;
export const BLOG_TITLE_GENERATOR_CATEGORY = 35;
export const BOOK_TITLE_GENERATOR_CATEGORY = 36;
export const CONTENT_TITLE_GENERATOR_CATEGORY = 37;
export const DOCUMENT_TITLE_GENERATOR_CATEGORY = 38;
export const LANDING_PAGE_TITLE_GENERATOR_CATEGORY = 39;
export const SOCIAL_MEDIA_POST_TITLE_GENERATOR_CATEGORY = 40;
export const STORY_TITLE_GENERATOR_CATEGORY = 41;
export const VIDEO_TITLE_GENERATOR_CATEGORY = 42;
export const PROMPT_GENERATOR_CATEGORY = 43;
export const TEXT_PROMPT_GENERATOR_CATEGORY = 44;
export const IMAGE_PROMPT_GENERATOR_CATEGORY = 45;
export const VIDEO_PROMPT_GENERATOR_CATEGORY = 46;
export const CODE_PROMPT_GENERATOR_CATEGORY = 47;
export const PARAGRAPH_GENERATOR_CATEGORY = 48;
export const INTRODUCTION_PARAGRAPH_GENERATOR_CATEGORY = 49;
export const BODY_PARAGRAPH_GENERATOR_CATEGORY = 50;
export const CONCLUSION_PARAGRAPH_GENERATOR_CATEGORY = 51;
export const VIDEO_SCRIPT_GENERATOR_CATEGORY = 52;
export const KEYWORD_GENERATOR_CATEGORY = 53;
export const SHORTEN_TEXT_CATEGORY = 54;
export const EXPAND_TEXT_CATEGORY = 55;

export const REPHRASE_PROMPT_PART = {
  type: PromptPartType.template,
  template: 'Rephrase a variant of the following text: "{{value}}".',
};

export const TONE_PROMPT_PART = {
  type: PromptPartType.template,
  template: ' Write in a {{value}} tone.',
};

const TONE_PROP = {
  value: 'tone',
  analyticsValue: 'tone',
  promptPart: TONE_PROMPT_PART,
};

export const CATEGORIES: AllCategories = {
  [CREATE_CATEGORY]: {
    id: CREATE_CATEGORY,
    label: 'Create',
    name: 'create',
    rephrase: true,
    hasOneResult: true,
  },
  [SHORTEN_TEXT_CATEGORY]: {
    id: SHORTEN_TEXT_CATEGORY,
    label: 'Shorten',
    name: 'shorten_text',
    path: {
      self: 'shorten-text',
      landing: '',
    },
    params: [
      {
        value: 'textToShorten',
        analyticsValue: 'textToShorten',
        promptPart: {
          type: PromptPartType.template,
          template: '{{value}}',
        },
      },
    ],
    systemRolePromptPart:
      'You are a text shortener for inputted text. Every time someone shortens the text you take the essentials from the text and reduce the text by 20%. The user can shorten the text multiple times (in parts of 20%).',
    model: GPT_VERSION,
    rephrase: false,
    hideGenerateMore: true,
    hasOneResult: true,
  },
  [EXPAND_TEXT_CATEGORY]: {
    id: EXPAND_TEXT_CATEGORY,
    label: 'Expand',
    name: 'expand_text',
    path: {
      self: 'expand-text',
      landing: '',
    },
    params: [
      TONE_PROP,
      {
        value: 'textToExpand',
        analyticsValue: 'textToExpand',
        promptPart: {
          type: PromptPartType.template,
          template: '{{value}}',
        },
      },
    ],
    systemRolePromptPart:
      'You are a text expander for inputted text. Every time someone expands the text you take the essentials from the text and expand the text. The user can expand the text multiple times.',
    model: GPT_VERSION,
    rephrase: false,
    hideGenerateMore: true,
    hasOneResult: true,
  },
  [KEYWORD_GENERATOR_CATEGORY]: {
    id: KEYWORD_GENERATOR_CATEGORY,
    label: 'Keywords',
    name: 'keywords_generator',
    path: {
      self: 'keyword-generator',
      landing: '',
    },
    params: [
      {
        value: 'keywordsGeneratorNumber',
        analyticsValue: 'video_script_complexity',
        promptPart: {
          type: PromptPartType.template,
          template: 'Generate {{value}} ',
        },
      },
      {
        value: 'keywordsGeneratorTopic',
        analyticsValue: 'keywords_generator_topic',
        promptPart: {
          type: PromptPartType.template,
          template: 'most relevant keywords for "{{value}}".',
        },
      },
    ],
    systemRolePromptPart:
      'You are a keyword generator for websites with landing pages, blog articles, and more. You generate keywords based on a topic. Use lowercase. Sort the keywords based on relevancy.',
    model: GPT_VERSION,
    rephrase: false,
    hasOneResult: true,
  },
  [IDEA_GENERATOR_CATEGORY]: {
    id: IDEA_GENERATOR_CATEGORY,
    label: 'Idea Generator',
    name: 'idea_generator',
    path: {
      self: 'idea-generator',
      landing: '',
    },
    params: [
      {
        value: 'ideaGeneratorTopic',
        analyticsValue: 'idea_generator',
        promptPart: {
          type: PromptPartType.template,
          template:
            'You are an idea generator that generates 4 ideas for a certain topic in a numbered list. Before generating the 4 ideas, use a short introduction on why these ideas are worth it. Keep it to 1 sentence each.\n\n Generate ideas for: "{{value}}". Generate it using simple markdown language.',
        },
      },
    ],
    model: GPT_VERSION,
    rephrase: false,
    hasOneResult: true,
  },
  [BLOG_IDEA_GENERATOR_CATEGORY]: {
    id: BLOG_IDEA_GENERATOR_CATEGORY,
    label: 'Blog topic generator',
    name: 'blog-idea-generator',
    path: {
      self: 'idea-generator/blog',
      landing: '',
    },
    params: [
      {
        value: 'blogIdeaGeneratorTopic',
        analyticsValue: 'blog-idea-generator',
        promptPart: {
          type: PromptPartType.template,
          template:
            'You are an idea generator that generates 4 ideas for a certain topic in a numbered list. Before generating the 4 ideas, use a short introduction on why these ideas are worth it. Keep it to 1 sentence each.\n\n Generate blog ideas for: "{{value}}". Generate it using simple markdown language.',
        },
      },
    ],
    model: GPT_VERSION,
    rephrase: false,
    hasOneResult: true,
  },
  [BOOK_IDEA_GENERATOR_CATEGORY]: {
    id: BOOK_IDEA_GENERATOR_CATEGORY,
    label: 'Book topic generator',
    name: 'book-idea-generator',
    path: {
      self: 'idea-generator/book',
      landing: '',
    },
    params: [
      {
        value: 'bookIdeaGeneratorTopic',
        analyticsValue: 'book-idea-generator',
        promptPart: {
          type: PromptPartType.template,
          template:
            'You are an idea generator that generates 4 ideas for a certain topic in a numbered list. Before generating the 4 ideas, use a short introduction on why these ideas are worth it. Keep it to 1 sentence each.\n\n Generate book ideas for: "{{value}}". Generate it using simple markdown language.',
        },
      },
    ],
    model: GPT_VERSION,
    rephrase: false,
    hasOneResult: true,
  },
  [BUSINESS_IDEA_GENERATOR_CATEGORY]: {
    id: BUSINESS_IDEA_GENERATOR_CATEGORY,
    label: 'Business idea generator',
    name: 'business-idea-generator',
    path: {
      self: 'idea-generator/business',
      landing: '',
    },
    params: [
      {
        value: 'businessIdeaGeneratorTopic',
        analyticsValue: 'business-idea-generator',
        promptPart: {
          type: PromptPartType.template,
          template:
            'You are an idea generator that generates 4 ideas for a certain topic in a numbered list. Before generating the 4 ideas, use a short introduction on why these ideas are worth it. Keep it to 1 sentence each.\n\n Generate business ideas for: "{{value}}". Generate it using simple markdown language.',
        },
      },
    ],
    model: GPT_VERSION,
    rephrase: false,
    hasOneResult: true,
  },
  [CONTENT_IDEA_GENERATOR_CATEGORY]: {
    id: CONTENT_IDEA_GENERATOR_CATEGORY,
    label: 'Content idea generator',
    name: 'content-idea-generator',
    path: {
      self: 'idea-generator/content',
      landing: '',
    },
    params: [
      {
        value: 'contentIdeaGeneratorTopic',
        analyticsValue: 'content-idea-generator',
        promptPart: {
          type: PromptPartType.template,
          template:
            'You are an idea generator that generates 4 ideas for a certain topic in a numbered list. Before generating the 4 ideas, use a short introduction on why these ideas are worth it. Keep it to 1 sentence each.\n\n Generate content ideas for: "{{value}}". Generate it using simple markdown language.',
        },
      },
    ],
    model: GPT_VERSION,
    rephrase: false,
    hasOneResult: true,
  },
  [DRAWING_IDEA_GENERATOR_CATEGORY]: {
    id: DRAWING_IDEA_GENERATOR_CATEGORY,
    label: 'Drawing idea generator',
    name: 'drawing-idea-generator',
    path: {
      self: 'idea-generator/drawing',
      landing: '',
    },
    params: [
      {
        value: 'drawingIdeaGeneratorTopic',
        analyticsValue: 'drawing-idea-generator',
        promptPart: {
          type: PromptPartType.template,
          template:
            'You are an idea generator that generates 4 ideas for a certain topic in a numbered list. Before generating the 4 ideas, use a short introduction on why these ideas are worth it. Keep it to 1 sentence each.\n\n Generate drawing ideas for: "{{value}}". Generate it using simple markdown language.',
        },
      },
    ],
    model: GPT_VERSION,
    rephrase: false,
    hasOneResult: true,
  },
  [GIFT_IDEA_GENERATOR_CATEGORY]: {
    id: GIFT_IDEA_GENERATOR_CATEGORY,
    label: 'Gift idea generator',
    name: 'gift-idea-generator',
    path: {
      self: 'idea-generator/gift',
      landing: '',
    },
    params: [
      {
        value: 'giftIdeaGeneratorTopic',
        analyticsValue: 'gift-idea-generator',
        promptPart: {
          type: PromptPartType.template,
          template:
            'You are an idea generator that generates 4 ideas for a certain topic in a numbered list. Before generating the 4 ideas, use a short introduction on why these ideas are worth it. Keep it to 1 sentence each.\n\n Generate gift ideas for: "{{value}}". Generate it using simple markdown language.',
        },
      },
    ],
    model: GPT_VERSION,
    rephrase: false,
    hasOneResult: true,
  },
  [PAINTING_IDEA_GENERATOR_CATEGORY]: {
    id: PAINTING_IDEA_GENERATOR_CATEGORY,
    label: 'Painting idea generator',
    name: 'painting-idea-generator',
    path: {
      self: 'idea-generator/painting',
      landing: '',
    },
    params: [
      {
        value: 'paintingIdeaGeneratorTopic',
        analyticsValue: 'painting-idea-generator',
        promptPart: {
          type: PromptPartType.template,
          template:
            'You are an idea generator that generates 4 ideas for a certain topic in a numbered list. Before generating the 4 ideas, use a short introduction on why these ideas are worth it. Keep it to 1 sentence each.\n\n Generate painting ideas for: "{{value}}". Generate it using simple markdown language.',
        },
      },
    ],
    model: GPT_VERSION,
    rephrase: false,
    hasOneResult: true,
  },
  [SIDE_HUSTLE_IDEA_GENERATOR_CATEGORY]: {
    id: SIDE_HUSTLE_IDEA_GENERATOR_CATEGORY,
    label: 'Side hustle idea generator',
    name: 'side-hustle-idea-generator',
    path: {
      self: 'idea-generator/side-hustle',
      landing: '',
    },
    params: [
      {
        value: 'sideHustleIdeaGeneratorTopic',
        analyticsValue: 'side-hustle-idea-generator',
        promptPart: {
          type: PromptPartType.template,
          template:
            'You are an idea generator that generates 4 ideas for a certain topic in a numbered list. Before generating the 4 ideas, use a short introduction on why these ideas are worth it. Keep it to 1 sentence each.\n\n Generate side hustle ideas for: "{{value}}". Generate it using simple markdown language.',
        },
      },
    ],
    model: GPT_VERSION,
    rephrase: false,
    hasOneResult: true,
  },
  [STARTUP_IDEA_GENERATOR_CATEGORY]: {
    id: STARTUP_IDEA_GENERATOR_CATEGORY,
    label: 'Startup idea generator',
    name: 'startup-idea-generator',
    path: {
      self: 'idea-generator/startup',
      landing: '',
    },
    params: [
      {
        value: 'startupIdeaGeneratorTopic',
        analyticsValue: 'startup-idea-generator',
        promptPart: {
          type: PromptPartType.template,
          template:
            'You are an idea generator that generates 4 ideas for a certain topic in a numbered list. Before generating the 4 ideas, use a short introduction on why these ideas are worth it. Keep it to 1 sentence each.\n\n Generate startup ideas for: "{{value}}". Generate it using simple markdown language.',
        },
      },
    ],
    model: GPT_VERSION,
    rephrase: false,
    hasOneResult: true,
  },
  [STORY_IDEA_GENERATOR_CATEGORY]: {
    id: STORY_IDEA_GENERATOR_CATEGORY,
    label: 'Story idea generator',
    name: 'story-idea-generator',
    path: {
      self: 'idea-generator/story',
      landing: '',
    },
    params: [
      {
        value: 'storyIdeaGeneratorTopic',
        analyticsValue: 'story-idea-generator',
        promptPart: {
          type: PromptPartType.template,
          template:
            'You are an idea generator that generates 4 ideas for a certain topic in a numbered list. Before generating the 4 ideas, use a short introduction on why these ideas are worth it. Keep it to 1 sentence each.\n\n Generate story ideas for: "{{value}}". Generate it using simple markdown language.',
        },
      },
    ],
    model: GPT_VERSION,
    rephrase: false,
    hasOneResult: true,
  },

  [TITLE_GENERATOR_CATEGORY]: {
    id: TITLE_GENERATOR_CATEGORY,
    label: 'Title Generator',
    name: 'title_generator',
    path: {
      self: 'title-generator',
      landing: '',
    },
    params: [
      {
        value: 'titleGeneratorTopic',
        analyticsValue: 'topic_of_title',
        promptPart: {
          type: PromptPartType.template,
          template: 'Generate title for {{value}}',
        },
      },
    ],
    systemRolePromptPart:
      'You are a title generator that generates a catchy but human-written title for a topic.',
    model: GPT_VERSION,
    rephrase: false,
    hasOneResult: false,
    generateMoreCount: 2,
  },
  [BLOG_TITLE_GENERATOR_CATEGORY]: {
    id: BLOG_TITLE_GENERATOR_CATEGORY,
    label: 'Title Generator',
    name: 'blog_title_generator',
    path: {
      self: 'title-generator/blog',
      landing: '',
    },
    params: [
      {
        value: 'blogTitleGeneratorTopic',
        analyticsValue: 'topic_of_title',
        promptPart: {
          type: PromptPartType.template,
          template: 'Generate a one line blog title for {{value}}',
        },
      },
    ],
    systemRolePromptPart:
      'You are a title generator that generates a catchy but human-written title for a topic.',
    model: GPT_VERSION,
    rephrase: false,
    hasOneResult: false,
    generateMoreCount: 2,
  },
  [BOOK_TITLE_GENERATOR_CATEGORY]: {
    id: BOOK_TITLE_GENERATOR_CATEGORY,
    label: 'Title Generator',
    name: 'title_generator',
    path: {
      self: 'title-generator/book',
      landing: '',
    },
    params: [
      {
        value: 'bookTitleGeneratorTopic',
        analyticsValue: 'topic_of_title',
        promptPart: {
          type: PromptPartType.template,
          template: 'Generate a one line book title for {{value}}',
        },
      },
    ],
    systemRolePromptPart:
      'You are a title generator that generates a catchy but human-written title for a topic.',
    model: GPT_VERSION,
    rephrase: false,
    hasOneResult: false,
    generateMoreCount: 2,
  },
  [CONTENT_TITLE_GENERATOR_CATEGORY]: {
    id: CONTENT_TITLE_GENERATOR_CATEGORY,
    label: 'Title Generator',
    name: 'title_generator',
    path: {
      self: 'title-generator/content',
      landing: '',
    },
    params: [
      {
        value: 'contentTitleGeneratorTopic',
        analyticsValue: 'topic_of_title',
        promptPart: {
          type: PromptPartType.template,
          template: 'Generate title for {{value}}',
        },
      },
    ],
    systemRolePromptPart:
      'You are a title generator that generates a catchy but human-written title for a topic.',
    model: GPT_VERSION,
    rephrase: false,
    hasOneResult: false,
    generateMoreCount: 2,
  },
  [DOCUMENT_TITLE_GENERATOR_CATEGORY]: {
    id: DOCUMENT_TITLE_GENERATOR_CATEGORY,
    label: 'Title Generator',
    name: 'title_generator',
    path: {
      self: 'title-generator/document',
      landing: '',
    },
    params: [
      {
        value: 'documentTitleGeneratorTopic',
        analyticsValue: 'topic_of_title',
        promptPart: {
          type: PromptPartType.template,
          template: 'Generate title for {{value}}',
        },
      },
    ],
    systemRolePromptPart:
      'You are a title generator that generates a catchy but human-written title for a topic.',
    model: GPT_VERSION,
    rephrase: false,
    hasOneResult: false,
    generateMoreCount: 2,
  },
  [LANDING_PAGE_TITLE_GENERATOR_CATEGORY]: {
    id: LANDING_PAGE_TITLE_GENERATOR_CATEGORY,
    label: 'Title Generator',
    name: 'title_generator',
    path: {
      self: 'title-generator/landing-page',
      landing: '',
    },
    params: [
      {
        value: 'landingPageTitleGeneratorTopic',
        analyticsValue: 'topic_of_title',
        promptPart: {
          type: PromptPartType.template,
          template: 'Generate title for {{value}}',
        },
      },
    ],
    systemRolePromptPart:
      'You are a title generator that generates a catchy but human-written title for a topic.',
    model: GPT_VERSION,
    rephrase: false,
    hasOneResult: false,
    generateMoreCount: 2,
  },
  [SOCIAL_MEDIA_POST_TITLE_GENERATOR_CATEGORY]: {
    id: SOCIAL_MEDIA_POST_TITLE_GENERATOR_CATEGORY,
    label: 'Title Generator',
    name: 'title_generator',
    path: {
      self: 'title-generator/social-media-post',
      landing: '',
    },
    params: [
      {
        value: 'socialMediaPostTitleGeneratorTopic',
        analyticsValue: 'topic_of_title',
        promptPart: {
          type: PromptPartType.template,
          template: 'Generate title for {{value}}',
        },
      },
    ],
    systemRolePromptPart:
      'You are a title generator that generates a catchy but human-written title for a topic.',
    model: GPT_VERSION,
    rephrase: false,
    hasOneResult: false,
    generateMoreCount: 2,
  },
  [STORY_TITLE_GENERATOR_CATEGORY]: {
    id: STORY_TITLE_GENERATOR_CATEGORY,
    label: 'Title Generator',
    name: 'title_generator',
    path: {
      self: 'title-generator/story',
      landing: '',
    },
    params: [
      {
        value: 'storyTitleGeneratorTopic',
        analyticsValue: 'topic_of_title',
        promptPart: {
          type: PromptPartType.template,
          template: 'Generate title for {{value}}',
        },
      },
    ],
    systemRolePromptPart:
      'You are a title generator that generates a catchy but human-written title for a topic.',
    model: GPT_VERSION,
    rephrase: false,
    hasOneResult: false,
    generateMoreCount: 2,
  },

  [VIDEO_SCRIPT_GENERATOR_CATEGORY]: {
    id: VIDEO_SCRIPT_GENERATOR_CATEGORY,
    label: 'Video Script Generator',
    name: 'video_script_generator',
    path: {
      self: 'video-script-generator',
      landing: '',
    },
    params: [
      {
        value: 'videoScriptLength',
        analyticsValue: 'length_of_video_script',
        promptPart: {
          type: PromptPartType.template,
          template: 'Generate a {{value}}',
        },
      },
      {
        value: 'videoScriptComplexity',
        analyticsValue: 'video_script_complexity',
        promptPart: {
          type: PromptPartType.template,
          template: ' {{value}} ',
        },
      },
      {
        value: 'videoScriptGeneratorTopic',
        analyticsValue: 'topic_of_video_script',
        promptPart: {
          type: PromptPartType.template,
          template: 'video script for {{value}}',
        },
      },
      {
        value: 'videoScriptStyle',
        analyticsValue: 'style_of_video_script',
        promptPart: {
          type: PromptPartType.template,
          template: ' with a {{value}} style.',
        },
      },
    ],
    systemRolePromptPart:
      "You are a high-quality video script generator, giving a script based on the user's input. Start with a Catchy title covering the video's topic. Then go to the video's script. Make sure to describe the film environment (+ tips on how to film it well), the approx. duration of a section, and add transitions with section titles. Consider the video duration, style, and the level of the video creator (beginner, intermediate, advanced). Use lightweight markdown language like Markdown or CommonMark.",
    model: GPT_VERSION,
    rephrase: true,
    hasOneResult: true,
    generateMoreCount: 1,
  },

  [VIDEO_TITLE_GENERATOR_CATEGORY]: {
    id: VIDEO_TITLE_GENERATOR_CATEGORY,
    label: 'Title Generator',
    name: 'title_generator',
    path: {
      self: 'title-generator/video',
      landing: '',
    },
    params: [
      {
        value: 'videoTitleGeneratorTopic',
        analyticsValue: 'topic_of_video_title',
        promptPart: {
          type: PromptPartType.template,
          template: 'Generate title for {{value}}',
        },
      },
    ],
    systemRolePromptPart:
      'You are a title generator that generates a catchy but human-written title for a topic.',
    model: GPT_VERSION,
    rephrase: false,
    hasOneResult: false,
    generateMoreCount: 2,
  },

  [ARTICLE_WRITER_CATEGORY]: {
    id: ARTICLE_WRITER_CATEGORY,
    label: 'Article Writer',
    name: 'article_writer',
    path: {
      self: 'article-writer',
      landing: '',
    },
    params: [
      {
        value: 'articleWriterTopic',
        analyticsValue: 'article_writer',
        promptPart: {
          type: PromptPartType.template,
          template:
            'Act as an Expert Article Writer to write an article of max. 800 words, 100% unique, creative, and human-like article of a minimum of 400 words using headings and sub-headings. The content should have headings and sub-headings (try to use LSI Keywords in headings). Use English for the main keyword. Make sure the article is plagiarism free. Don\'t forget to use a question mark (?) at the end of questions.\n\nGenerate an article with the keywords: "{{value}}". use lightweight markdown language like Markdown or CommonMark.',
        },
      },
      {
        value: 'articleWriterDetails',
        analyticsValue: 'article_writer_details',
        promptPart: {
          type: PromptPartType.template,
          template: ' and the following details and/or outline: {{value}}.',
        },
      },
      {
        value: 'seoCheckboxValue',
        analyticsValue: 'article_writer_seo',
        promptPart: {
          type: PromptPartType.string,
          template:
            " This article should show the main keyword's experience, expertise, authority, and trust. Try to use contractions, idioms, transitional phrases, interjections, dangling modifiers, and colloquialisms, and avoid repetitive phrases and unnatural sentence structures. Make sure to use natural space/line breaks in between sentences when sections get longer than 2 sentences max. When you write, you will correctly format the blog post according to proper SEO standards, with as much rich and detailed HTML as possible, for example, lists, bold, italics, and tables. Try to ask questions and then immediately give a good and concise answer, to try to achieve the featured snippet on Google. The article should include an SEO meta-description of max. 155 characters (use the main keyword in the description), an introduction, and a click-worthy short title. Also, use the seed keyword as the first H2. Always use a combination of paragraphs, lists, and tables for a better reader experience. Write at least one paragraph with the heading of the main keyword.",
        },
      },
    ],
    rephrasePromptPart: {
      type: PromptPartType.string,
      template: ' Keep the markdown format.',
    },
    model: GPT_VERSION,
    rephrase: true,
    hasOneResult: true,
    hideGenerateMore: true,
  },
  [GRAMMAR_CHECKER_CATEGORY]: {
    id: GRAMMAR_CHECKER_CATEGORY,
    label: 'Grammar Checker',
    name: 'grammar_checker',
    path: {
      self: 'grammar-checker',
      landing: '',
    },
    params: [
      {
        value: 'textToGrammarCheck',
        analyticsValue: 'textToGrammarCheck',
        promptPart: {
          type: PromptPartType.template,
          template: 'Correct this to standard English:\n\n {{value}}.',
        },
      },
    ],
    hasOneResult: true,
    hideGenerateMore: true,
    model: GPT_VERSION,
    rephrase: true,
  },
  [TRANSLATOR_CATEGORY]: {
    id: TRANSLATOR_CATEGORY,
    label: 'Translator',
    name: 'translator',
    path: {
      self: 'translator',
      landing: '',
    },
    params: [
      {
        value: 'inputValue',
        analyticsValue: 'input_value',
        promptPart: {
          type: PromptPartType.template,
          template:
            'You are a translator, and you correctly translate languages. You can detect and translate the following languages: Arabic, Chinese, Dutch, English, French, German, Indonesian, Japanese, Malaysian, Persian, Polish, Portuguese, Russian, Spanish, Turkish, and Vietnamese. Be consistent with your translations.\nTranslate "{{value}}"',
        },
      },
      {
        value: 'dropdown01',
        analyticsValue: 'from_language',
        promptPart: {
          type: PromptPartType.template,
          template: ' from {{value}}',
        },
      },
      {
        value: 'dropdown02',
        analyticsValue: 'to_language',
        promptPart: {
          type: PromptPartType.template,
          template: ' to {{value}}',
        },
      },
    ],
    model: GPT_VERSION,
    rephrase: true,
    hasOneResult: true,
    excludeFinalPromptPart: true,
    hideGenerateMore: true,
    formatResults: (input) => input.replace(/^"/, '').replace(/"$/, ''),
  },
  [STORY_GENERATOR_CATEGORY]: {
    id: STORY_GENERATOR_CATEGORY,
    label: 'Story Generator',
    name: 'story_generator',
    path: {
      self: 'story-generator',
      landing: '',
    },
    params: [
      {
        value: 'length',
        analyticsValue: 'length',
        promptPart: {
          type: PromptPartType.template,
          template: 'Generate a {{value}} story',
        },
      },
      {
        value: 'describeStory',
        analyticsValue: 'describe_story',
        promptPart: {
          type: PromptPartType.template,
          template: ' about {{value}}',
        },
      },
      {
        value: 'creativity',
        analyticsValue: 'creativity',
        promptPart: {
          type: PromptPartType.template,
          template: ' with {{value}} creativity.',
        },
      },
    ],
    systemRolePromptPart:
      "You are a story generator that generates 1 story for a certain topic. You consider the story's style, length (short, medium, long), and creativity (low, medium, high). Make use of good English. use lightweight markdown language like Markdown or CommonMark. Don't start with \"Once upon a time\". And don't generate the same results.",
    hasOneResult: true,
    hideGenerateMore: false,
    model: GPT_VERSION,
    rephrase: true,
  },
  [ESSAY_WRITER_CATEGORY]: {
    id: ESSAY_WRITER_CATEGORY,
    label: 'Essay Writer',
    name: 'essay_writer',
    path: {
      self: 'essay-writer',
      landing: '',
    },
    params: [
      {
        value: 'essayInput',
        analyticsValue: 'essay_topic',
        promptPart: {
          type: PromptPartType.template,
          template: 'Write an essay about {{value}}',
        },
      },
      {
        value: 'essayDetails',
        analyticsValue: 'essay_details',
        promptPart: {
          type: PromptPartType.template,
          template: ' and include the following details: {{value}}',
        },
      },
    ],
    systemRolePromptPart:
      "You are a student, and you are writing an essay about a topic using simple markdown language, without links, footnotes or citations. First, you generate an outline based on the topic and use that as the structure for the essay. Use max. 1500 words. Make limited use of citations and references. Focus on good grammar and English. Make it sound human, objective, and precise. Don't use romanticizing words. Make it plagiarism free.",
    hasOneResult: true,
    hideGenerateMore: true,
    model: GPT_VERSION,
    rephrase: true,
  },
  [AD_WRITER_CATEGORY]: {
    id: AD_WRITER_CATEGORY,
    label: 'Ad Copy',
    name: 'ad_writer',
    path: {
      self: 'ads-copy-generator',
      landing: 'ads-copy-generator',
    },
    params: [
      {
        value: 'adWriterRadioValue',
        analyticsValue: 'ad_writer_radio',
        promptPart: {
          type: PromptPartType.stringVariants,
          variants: {
            body: 'Write 3 variants of an ad',
            headline: 'Write 3 variants of a google ad headline',
            description: 'Write 3 variants of a google ad description',
          },
        },
      },
      {
        value: 'product',
        analyticsValue: 'product',
        promptPart: {
          type: PromptPartType.template,
          template: ' for {{value}}',
        },
      },
      {
        value: 'description',
        analyticsValue: 'description',
        promptPart: {
          type: PromptPartType.template,
          template: '  The brand describes themselves as: {{value}}.',
        },
      },
      {
        value: 'businessType',
        analyticsValue: 'business_type',
        promptPart: {
          type: PromptPartType.template,
          template: ' The brand is a {{value}} business.',
        },
      },
      {
        value: 'specialOffers',
        analyticsValue: 'special_offers',
        promptPart: {
          type: PromptPartType.template,
          template: ' Include the special offer of {{value}}.',
        },
      },
      {
        value: 'callToAction',
        analyticsValue: 'call_to_action',
        promptPart: {
          type: PromptPartType.template,
          template: ' Add a call to action to {{value}}.',
        },
      },
      TONE_PROP,
      {
        value: 'characterLimit',
        noTrim: true,
        analyticsValue: 'character_limit',
        promptPart: {
          type: PromptPartType.template,
          template: ' Write the ad in less than {{value}} characters.',
        },
      },
      {
        value: 'language',
        analyticsValue: 'language',
        promptPart: {
          type: PromptPartType.template,
          template: ' Write it in {{value}}.',
        },
      },
    ],
    model: GPT_VERSION,
    rephrase: true,
    splitChoices: true,
  },
  [SOCIAL_MEDIA_BIO_CATEGORY]: {
    id: SOCIAL_MEDIA_BIO_CATEGORY,
    label: 'Social Media Bio',

    name: 'social_media_bio',
    path: {
      self: 'bio-generator',
      landing: 'bio-generator',
    },
    params: [
      {
        value: 'tellUsAboutYourself',
        analyticsValue: 'tell_us_about_yourself',
        promptPart: {
          type: PromptPartType.template,
          template:
            'Write a variant of a social media bio for someone who describes themselves like this: {{value}}. use lightweight markdown language like Markdown or CommonMark.',
        },
      },
      TONE_PROP,
      {
        value: 'callToAction',
        analyticsValue: 'call_to_action',
        promptPart: {
          type: PromptPartType.template,
          template: ' Add a call to action to {{value}}.',
        },
      },
    ],
    model: GPT_VERSION,
    rephrase: true,
  },
  [REPHRASER_CATEGORY]: {
    id: REPHRASER_CATEGORY,
    label: 'Paraphraser',

    name: 'rephrase',
    path: {
      self: 'paraphrase',
      landing: 'paraphrase',
    },
    params: [
      {
        value: 'textToParaphrase',
        analyticsValue: 'text_to_paraphrase',
        promptPart: {
          type: PromptPartType.template,
          template:
            'Rephrase a variant of the following text: "{{value}}". use lightweight markdown language like Markdown or CommonMark.',
        },
      },
      TONE_PROP,
    ],
    model: GPT_VERSION,
    rephrase: false,
  },
  [MARKETING_SLOGAN_CATEGORY]: {
    id: MARKETING_SLOGAN_CATEGORY,
    label: 'Slogan Generator',
    name: 'marketing_slogan',
    path: {
      self: 'slogan-maker',
      landing: 'slogan-maker',
    },
    params: [
      {
        value: 'sloganWords',
        analyticsValue: 'logan_words',
        promptPart: {
          type: PromptPartType.template,
          template: 'Generate a slogan for {{value}}',
        },
      },
      {
        value: 'sloganPurpose',
        analyticsValue: 'slogan_purpose',
        promptPart: {
          type: PromptPartType.template,
          template: 'as an {{value}}',
        },
      },
      TONE_PROP,
    ],
    systemRolePromptPart:
      'You are a creative and smart marketer that wants to create a slogan for a product, brand, company, person, advertisement, or campaign. You generate high-quality, creative slogan that fits the goal of the input.',
    model: GPT_VERSION,
    generateCount: 5,
    rephrase: true,
  },
  [BUSINESS_NAME_CATEGORY]: {
    id: BUSINESS_NAME_CATEGORY,
    label: 'Business Name',
    name: 'business_name',
    path: {
      self: 'business-name-generator',
      landing: 'business-name-generator',
    },
    params: [
      {
        value: 'description',
        analyticsValue: 'description',
        promptPart: {
          type: PromptPartType.template,
          template:
            'Write 3 variants of a business name for a brand who describes themselves as: {{value}}.',
        },
      },
      TONE_PROP,
      {
        value: 'language',
        analyticsValue: 'language',
        promptPart: {
          type: PromptPartType.template,
          template: ' Write it in {{value}}.',
        },
      },
      {
        value: 'characterLimit',
        noTrim: true,
        analyticsValue: 'character_limit',
        promptPart: {
          type: PromptPartType.template,
          template: ' Write the ad in less than {{value}} characters.',
        },
      },
    ],
    model: GPT_VERSION,
    rephrase: true,
    splitChoices: true,
  },
  [INSTAGRAM_HASHTAG_CATEGORY]: {
    id: INSTAGRAM_HASHTAG_CATEGORY,
    label: 'Hashtag Ideas',
    name: 'hashtag_ideas',
    path: {
      self: 'instagram-hashtag-generator',
      landing: 'instagram-hashtag-generator',
    },
    params: [
      {
        value: 'numberOfResults',
        analyticsValue: 'number_of_results',
        noTrim: true,
        promptPart: {
          type: PromptPartType.template,
          template: 'Write {{value}} instagram hashtag options',
        },
      },
      {
        label: 'word to start',
        value: 'wordToStart',
        analyticsValue: 'word_to_start',
        promptPart: {
          type: PromptPartType.template,
          template: ' for the keywords: {{value}}.',
        },
      },
    ],
    model: GPT_VERSION,
    rephrase: false,
    hasOneResult: true,
  },
  [PRODUCT_DESCRIPTION_CATEGORY]: {
    id: PRODUCT_DESCRIPTION_CATEGORY,
    label: 'Product Description',

    name: 'product_description',
    path: {
      self: 'product-description-generator',
      landing: 'product-description-generator',
    },
    params: [
      {
        value: 'product',
        analyticsValue: 'product',
        promptPart: {
          type: PromptPartType.template,
          template:
            'Write 3 variants of a product description for {{value}}. Do not use any hashtags or # symbols in your response. use lightweight markdown language like Markdown or CommonMark.',
        },
      },
      {
        value: 'keywords',
        analyticsValue: 'keywords',
        promptPart: {
          type: PromptPartType.template,
          template: ' Include the keywords: {{value}}.',
        },
      },
      {
        value: 'businessType',
        analyticsValue: 'business_type',
        promptPart: {
          type: PromptPartType.template,
          template: ' The brand is a {{value}} business.',
        },
      },
      TONE_PROP,
      {
        value: 'characterLimit',
        noTrim: true,
        analyticsValue: 'character_limit',
        promptPart: {
          type: PromptPartType.template,
          template: ' Write the description in less than {{value}} characters.',
        },
      },
      {
        value: 'language',
        analyticsValue: 'language',
        promptPart: {
          type: PromptPartType.template,
          template: ' Write it in {{value}}.',
        },
      },
    ],
    additionalPromptPart: {
      type: PromptPartType.string,
      template: ' Do not use any hashtags or # symbols in your response.',
    },
    model: GPT_VERSION,
    rephrase: true,
    splitChoices: true,
  },

  [EMAIL_WRITER_CATEGORY]: {
    id: EMAIL_WRITER_CATEGORY,
    label: 'Email Writer',

    name: 'email_writer',
    path: {
      self: 'email-writer',
      landing: 'email-writer',
    },
    params: [
      {
        value: 'purpose',
        analyticsValue: 'purpose',
        promptPart: {
          type: PromptPartType.template,
          template: 'Using simple markdown language, create an email about: "{{value}}"',
        },
      },
      {
        value: 'description',
        analyticsValue: 'description',
        promptPart: {
          type: PromptPartType.template,
          template: ' and include {{value}}.',
        },
      },
      TONE_PROP,
    ],
    model: GPT_VERSION,
    rephrase: true,
    hasOneResult: true,
  },
  [INSTAGRAM_CAPTION_CATEGORY]: {
    id: INSTAGRAM_CAPTION_CATEGORY,
    label: 'Image Caption Generator',
    name: 'instagram_caption',
    path: {
      self: 'instagram-caption-generator',
      landing: 'instagram-caption-generator',
    },
    params: [
      {
        value: 'describeYourImage',
        analyticsValue: 'describe_your_image',
        promptPart: {
          type: PromptPartType.template,
          template: 'Write an engaging caption for the following image: "{{value}}".',
        },
      },
      {
        value: 'captionDetails',
        analyticsValue: 'caption_details',
        promptPart: {
          type: PromptPartType.template,
          template: ' Include the following details in the captions: {{value}}. ',
        },
      },
      {
        value: 'characterLimit',
        noTrim: true,
        analyticsValue: 'character_limit',
        promptPart: {
          type: PromptPartType.template,
          template: ' Write it in less than {{value}} characters.',
        },
      },
      {
        value: 'callToAction',
        analyticsValue: 'callToAction',
        promptPart: {
          type: PromptPartType.template,
          template: " It's promoting: {{value}}.",
        },
      },
      TONE_PROP,
    ],
    additionalPromptPart: {
      type: PromptPartType.string,
      template:
        ' Include a one or two relevant emojis in the post. Add a new line after each idea.',
    },
    model: GPT_VERSION,
    rephrase: true,
  },
  [PAGE_OUTLINE_CATEGORY]: {
    id: PAGE_OUTLINE_CATEGORY,
    label: 'Outline Generator',
    name: 'page_outline',
    path: {
      self: 'landing-page-outline',
      landing: 'meta-description-generator',
    },
    params: [
      {
        value: 'describeOutline',
        analyticsValue: 'describe_outline',
        promptPart: {
          type: PromptPartType.template,
          template: 'Using simple markdown language, generate an outline of {{value}}',
        },
      },
      {
        value: 'outlinePurpose',
        analyticsValue: 'outline_purpose',
        promptPart: {
          type: PromptPartType.template,
          template: ' for a {{value}}.',
        },
      },
      {
        value: 'includeIdeas',
        analyticsValue: 'include_ideas',
        promptPart: {
          type: PromptPartType.template,
          template: 'and include details about {{value}}',
        },
      },

      TONE_PROP,
    ],
    systemRolePromptPart:
      "You are an experienced writer and must create an outline in preparation for the content you are about to write. Make use of titles, subtitles, and sub-subtitles. Use bullet points and numbered lists. Enhance and enlarge the outline's topic with other very similar topics and define the number of topics and suggestions based on the length of the outline's purpose (a book is lots of additional topics and suggestions, but a LinkedIn post should be more precise).",
    model: GPT_VERSION,
    rephrase: true,
    hasOneResult: true,
  },
  [META_DATA_MAKER_CATEGORY]: {
    id: META_DATA_MAKER_CATEGORY,
    label: 'Meta Description Generator',
    name: 'meta_data_maker',
    path: {
      self: 'meta-description-generator',
      landing: '',
    },
    params: [
      {
        value: 'describeTopic',
        analyticsValue: 'describe_topic',
        promptPart: {
          type: PromptPartType.template,
          template: 'Generate a meta description for {{value}}',
        },
      },
      {
        value: 'keywords',
        analyticsValue: 'keywords',
        promptPart: {
          type: PromptPartType.template,
          template: '. Use the main keyword {{value}}',
        },
      },
      {
        value: 'product',
        analyticsValue: 'product',
        promptPart: {
          type: PromptPartType.template,
          template: '. Include {{value}} as the name of the company/product/service.',
        },
      },
      {
        value: 'metaTitleCheckbox',
        analyticsValue: 'meta_title_checkbox',
        promptPart: {
          type: PromptPartType.string,
          template: '. Include a meta title as well. Add a new line after meta title.',
        },
      },
    ],
    systemRolePromptPart:
      'You are a digital marketer and want to rank first in the Google rankings. You have a landing page or article that needs metadata to display in the Google search results. You generate an option between 140-160 characters. Focus on answering the main keyword but write it like a human, informative and persuasive.',
    model: GPT_VERSION,
    rephrase: true,
    hasOneResult: false,
    rephrasePromptPart: {
      type: PromptPartType.string,
      template: ' Keep the same structure of metadata.',
    },
  },
  [SUMMARIZER_CATEGORY]: {
    id: SUMMARIZER_CATEGORY,
    label: 'Summarizer',
    name: 'summarizer',
    path: {
      self: 'summarizer',
      landing: '',
    },
    params: [
      {
        value: 'textToBeSummarised',
        analyticsValue: 'text_to_be_summarised',
        promptPart: {
          type: PromptPartType.template,
          template: '"{{value}}"',
        },
      },
      {
        value: 'format',
        analyticsValue: 'format',
        promptPart: {
          type: PromptPartType.template,
          template:
            'Using simple markdown language format, summarise the following text into {{value}}.',
        },
      },
      {
        value: 'textPurpose',
        analyticsValue: 'text_purpose',
        promptPart: {
          type: PromptPartType.template,
          template: ' This text has the following purpose, type and/or origin: {{value}}.',
        },
      },
      {
        value: 'describeText',
        analyticsValue: 'describe_text',
        promptPart: {
          type: PromptPartType.template,
          template: ' The indented audience and/or purpose for the output is {{value}}.',
        },
      },
    ],
    model: GPT_VERSION,
    rephrase: true,
    hasOneResult: true,
    rephrasePromptPart: {
      type: PromptPartType.string,
      template: ' Keep the same format (with bullet points or paragraphs)',
    },
  },
  [CREATE_CATEGORY]: {
    id: CREATE_CATEGORY,
    label: 'Create',
    name: 'create',
    path: {
      self: 'create',
      landing: '',
    },
    systemRolePromptPart:
      'You are an AI Text generator that generates short text (max. 200 words) for a certain need.',
    params: [
      {
        value: 'createInput',
        analyticsValue: 'create_input',
        promptPart: {
          type: PromptPartType.template,
          template: 'Generate "{{value}}"',
        },
      },
      {
        value: 'createDetails',
        analyticsValue: 'create_details',
        promptPart: {
          type: PromptPartType.template,
          template: ' with the following details: "{{value}}"',
        },
      },
    ],
    hasOneResult: true,
    hideGenerateMore: false,
    model: GPT_VERSION,
    rephrase: true,
  },
  [PROMPT_GENERATOR_CATEGORY]: {
    id: PROMPT_GENERATOR_CATEGORY,
    label: 'Prompt Generator',
    name: 'prompt_generator',
    path: {
      self: 'prompt-generator',
      landing: '',
    },
    systemRolePromptPart:
      "You are a high-quality prompt generator, giving 1 prompt based on a user's input. The prompt can be used in any AI model to generate the output that the user wants to get. The idea is to make it easier for people to create what they like with minimal input. Make sure to include the user input in the results so that when pasting the prompt in other platforms, the model has enough context to create good results. Don't explain it like a story; keep it shorter and technical, like a prompt. Write humanly.",
    params: [
      {
        value: 'length',
        analyticsValue: 'length',
        promptPart: {
          type: PromptPartType.template,
          template: 'Write a {{value}} prompt',
        },
      },
      {
        value: 'promptGeneratorTopic',
        analyticsValue: 'prompt_generator_topic',
        promptPart: {
          type: PromptPartType.template,
          template: ' about "{{value}}"',
        },
      },
      {
        value: 'promptGeneratorDetails',
        analyticsValue: 'prompt_generator_details',
        promptPart: {
          type: PromptPartType.template,
          template: ', and include the following details: "{{value}}".',
        },
      },
    ],
    model: GPT_VERSION,
    rephrase: true,
    hasOneResult: true,
  },
  [TEXT_PROMPT_GENERATOR_CATEGORY]: {
    id: TEXT_PROMPT_GENERATOR_CATEGORY,
    label: 'Text prompt generator',
    name: 'text-prompt-generator',
    path: {
      self: 'prompt-generator/text',
      landing: '',
    },
    systemRolePromptPart:
      "You are a high-quality prompt generator, giving 1 prompt based on a user's input. The prompt can be used in any AI model to generate the output that the user wants to get. The idea is to make it easier for people to create what they like with minimal input. Make sure to include the user input in the results so that when pasting the prompt in other platforms, the model has enough context to create good results. Don't explain it like a story; keep it shorter and technical, like a prompt. Write humanly.",
    params: [
      {
        value: 'length',
        analyticsValue: 'length',
        promptPart: {
          type: PromptPartType.template,
          template: 'Write a {{value}} prompt',
        },
      },
      {
        value: 'textPromptGeneratorTopic',
        analyticsValue: 'text_prompt_generator_topic',
        promptPart: {
          type: PromptPartType.template,
          template: ' about "{{value}}"',
        },
      },
      {
        value: 'textPromptGeneratorDetails',
        analyticsValue: 'text_prompt_generator_details',
        promptPart: {
          type: PromptPartType.template,
          template: ', and include the following details: "{{value}}".',
        },
      },
    ],
    model: GPT_VERSION,
    rephrase: true,
    hasOneResult: true,
  },
  [IMAGE_PROMPT_GENERATOR_CATEGORY]: {
    id: IMAGE_PROMPT_GENERATOR_CATEGORY,
    label: 'Image prompt generator',
    name: 'image-prompt-generator',
    path: {
      self: 'prompt-generator/image',
      landing: '',
    },
    systemRolePromptPart: `You are a high-quality image prompt generator, giving 1 prompt based on a user\'s input. The prompt can be used in text-to-image AI models to generate the image that the user wants to get.
       Follow these rules: \n
      A prompt consists of three parts: subject, descriptors, and styles. \n
      Commas separate all words and phrases. \n
      The closer to the beginning of a prompt a word/phrase is, the more likely it will be included in the image. \n
      If not enough detail from the user input is provided, use semantically related subjects, descriptors, and styles. \n
      Brackets can be used to indicate the importance of a word or phrase. Adding multiple brackets increases its importance.  \n
      Sometimes, you can add numbers at the end of a word/phrase to increase its prompt. \n
      Below are examples of high-quality prompts. Use these as a guideline only. Examples: \n
      a high quality professional photo of a single (SKF) ball (((bearing))) standing on a concrete table in front of a city skyline, ((stainless steel)), product photography, shallow depth of field, realistic, front view, close up, product image, bokeh, octane render, [trending on artstation], cinematic, high detail, octane render, 8k \n
      a high quality professional photo of a watch standing on a concrete table in front of a city skyline, product photography, shallow depth of field, realistic, front view, close up, product image, bokeh. octane render, trending on art station, symmetrical artwork. cinematic, high detail, octane render, 8k \n
      The idea is to make it easier for people to create what they like with minimal input. Make sure to include the user input in the results so that when pasting the prompt in other platforms, the model has enough context to create good results.`,
    params: [
      {
        value: 'length',
        analyticsValue: 'length',
        promptPart: {
          type: PromptPartType.template,
          template: 'Write a {{value}} prompt',
        },
      },
      {
        value: 'imagePromptGeneratorTopic',
        analyticsValue: 'image_prompt_generator_topic',
        promptPart: {
          type: PromptPartType.template,
          template: ' about "{{value}}"',
        },
      },
      {
        value: 'imagePromptGeneratorDetails',
        analyticsValue: 'image_prompt_generator_details',
        promptPart: {
          type: PromptPartType.template,
          template: ', and include the following details: "{{value}}".',
        },
      },
    ],
    model: GPT_VERSION,
    rephrase: true,
    hasOneResult: true,
  },
  [VIDEO_PROMPT_GENERATOR_CATEGORY]: {
    id: VIDEO_PROMPT_GENERATOR_CATEGORY,
    label: 'Video prompt generator',
    name: 'video-prompt-generator',
    path: {
      self: 'prompt-generator/video',
      landing: '',
    },
    systemRolePromptPart: `You are a high-quality video prompt generator, giving 1 prompt based on a user's input. The prompt can be used in text-to-video AI models to generate the video that the user wants to get. \n
      Follow these rules:\n
      A prompt consists of three parts: subject, descriptors, and styles.\n
      Commas separate all words and phrases.\n
      The closer to the beginning of a prompt a word/phrase is, the more likely it will be included in the image.\n
      If not enough detail from the user input is provided, use semantically related subjects, descriptors, and styles.\n
      Brackets can be used to indicate the importance of a word or phrase. Adding multiple brackets increases its importance.\n
      Sometimes, you can add numbers at the end of a word/phrase to increase its prompt.\n
      Below are examples of high-quality prompts. Use these as a guideline only. Examples:\n
      a high quality professional photo of a single (SKF) ball (((bearing))) standing on a concrete table in front of a city skyline, ((stainless steel)), product photography, shallow depth of field, realistic, front view, close up, product image, bokeh, octane render, [trending on artstation], cinematic, high detail, octane render, 8k\n
      a high quality professional photo of a watch standing on a concrete table in front of a city skyline, product photography, shallow depth of field, realistic, front view, close up, product image, bokeh. octane render, trending on art station, symmetrical artwork. cinematic, high detail, octane render, 8k\n
      The idea is to make it easier for people to create what they like with minimal input. Make sure to include the user input in the results so that when pasting the prompt in other platforms, the model has enough context to create good results.`,
    params: [
      {
        value: 'length',
        analyticsValue: 'length',
        promptPart: {
          type: PromptPartType.template,
          template: 'Write a {{value}} prompt',
        },
      },
      {
        value: 'videoPromptGeneratorTopic',
        analyticsValue: 'video_prompt_generator_topic',
        promptPart: {
          type: PromptPartType.template,
          template: ' about "{{value}}"',
        },
      },
      {
        value: 'videoPromptGeneratorDetails',
        analyticsValue: 'video_prompt_generator_details',
        promptPart: {
          type: PromptPartType.template,
          template: ', and include the following details: "{{value}}".',
        },
      },
    ],
    model: GPT_VERSION,
    rephrase: true,
    hasOneResult: true,
  },
  [CODE_PROMPT_GENERATOR_CATEGORY]: {
    id: CODE_PROMPT_GENERATOR_CATEGORY,
    label: 'Code prompt generator',
    name: 'code-prompt-generator',
    path: {
      self: 'prompt-generator/code',
      landing: '',
    },
    systemRolePromptPart:
      "You are a high-quality software code prompt generator, giving 1 prompt based on a user's input. The prompt can be used in any AI model to generate the code that the user wants to get. The idea is to make it easier for people to create what they like with minimal input. Make sure to include the user input in the results so that when pasting the prompt in other platforms, the model has enough context to create good results. Don't explain it like a story; keep it shorter and technical, like a prompt. Write humanly.",
    params: [
      {
        value: 'length',
        analyticsValue: 'length',
        promptPart: {
          type: PromptPartType.template,
          template: 'Write a {{value}} prompt',
        },
      },
      {
        value: 'codePromptGeneratorTopic',
        analyticsValue: 'code_prompt_generator_topic',
        promptPart: {
          type: PromptPartType.template,
          template: ' about "{{value}}"',
        },
      },
      {
        value: 'codePromptGeneratorDetails',
        analyticsValue: 'code_prompt_generator_details',
        promptPart: {
          type: PromptPartType.template,
          template: ', and include the following details: "{{value}}".',
        },
      },
    ],
    model: GPT_VERSION,
    rephrase: true,
    hasOneResult: true,
  },
  [PARAGRAPH_GENERATOR_CATEGORY]: {
    id: PARAGRAPH_GENERATOR_CATEGORY,
    label: 'Paragraph Generator',
    name: 'paragraph_generator',
    path: {
      self: 'paragraph-generator',
      landing: '',
    },
    systemRolePromptPart:
      "You are a high-quality paragraph generator, giving 1 paragraph based on the user's input. Focus on answering the paragraph's topic and apply the tone of voice. Make it human-written and easily readable.",
    params: [
      {
        value: 'length',
        analyticsValue: 'length',
        promptPart: {
          type: PromptPartType.template,
          template: 'Write a {{value}} paragraph',
        },
      },
      {
        value: 'paragraphGeneratorTopic',
        analyticsValue: 'paragraph_generator_topic',
        promptPart: {
          type: PromptPartType.template,
          template: ' about "{{value}}"',
        },
      },
      {
        value: 'tone',
        analyticsValue: 'tone',
        promptPart: {
          type: PromptPartType.template,
          template: ' with "{{value}} tone of voice"',
        },
      },
      {
        value: 'paragraphGeneratorDetails',
        analyticsValue: 'paragraph_generator_details',
        promptPart: {
          type: PromptPartType.template,
          template: ' and include the following details: "{{value}}".',
        },
      },
    ],
    model: GPT_VERSION,
    rephrase: true,
    hasOneResult: true,
  },
  [INTRODUCTION_PARAGRAPH_GENERATOR_CATEGORY]: {
    id: INTRODUCTION_PARAGRAPH_GENERATOR_CATEGORY,
    label: 'Introduction Paragraph Generator',
    name: 'introduction_paragraph_generator',
    path: {
      self: 'paragraph-generator/introduction',
      landing: '',
    },
    systemRolePromptPart:
      "You are a high-quality introduction paragraph generator, giving 1 paragraph based on the user's input. Focus on answering the paragraph's topic and apply the tone of voice. Make it human-written and easily readable.",
    params: [
      {
        value: 'length',
        analyticsValue: 'length',
        promptPart: {
          type: PromptPartType.template,
          template: 'Write a {{value}} paragraph',
        },
      },
      {
        value: 'paragraphGeneratorTopic',
        analyticsValue: 'paragraph_generator_topic',
        promptPart: {
          type: PromptPartType.template,
          template: ' about "{{value}}"',
        },
      },
      {
        value: 'tone',
        analyticsValue: 'tone',
        promptPart: {
          type: PromptPartType.template,
          template: ' with "{{value}} tone of voice"',
        },
      },
      {
        value: 'paragraphGeneratorDetails',
        analyticsValue: 'paragraph_generator_details',
        promptPart: {
          type: PromptPartType.template,
          template: ' and include the following details: "{{value}}".',
        },
      },
    ],
    model: GPT_VERSION,
    rephrase: false,
    hasOneResult: true,
  },
  [BODY_PARAGRAPH_GENERATOR_CATEGORY]: {
    id: BODY_PARAGRAPH_GENERATOR_CATEGORY,
    label: 'Body Paragraph Generator',
    name: 'body_paragraph_generator',
    path: {
      self: 'paragraph-generator/body',
      landing: '',
    },
    systemRolePromptPart:
      "You are a high-quality body paragraph generator, giving 1 paragraph based on the user's input. Focus on answering the paragraph's topic and apply the tone of voice. Make it human-written and easily readable.",
    params: [
      {
        value: 'length',
        analyticsValue: 'length',
        promptPart: {
          type: PromptPartType.template,
          template: 'Write a {{value}} paragraph',
        },
      },
      {
        value: 'paragraphGeneratorTopic',
        analyticsValue: 'paragraph_generator_topic',
        promptPart: {
          type: PromptPartType.template,
          template: ' about "{{value}}"',
        },
      },
      {
        value: 'tone',
        analyticsValue: 'tone',
        promptPart: {
          type: PromptPartType.template,
          template: ' with "{{value}} tone of voice"',
        },
      },
      {
        value: 'paragraphGeneratorDetails',
        analyticsValue: 'paragraph_generator_details',
        promptPart: {
          type: PromptPartType.template,
          template: ' and include the following details: "{{value}}".',
        },
      },
    ],
    model: GPT_VERSION,
    rephrase: true,
    hasOneResult: true,
  },
  [CONCLUSION_PARAGRAPH_GENERATOR_CATEGORY]: {
    id: CONCLUSION_PARAGRAPH_GENERATOR_CATEGORY,
    label: 'Conclusion Paragraph Generator',
    name: 'conclusion_paragraph_generator',
    path: {
      self: 'paragraph-generator/conclusion',
      landing: '',
    },
    systemRolePromptPart:
      "You are a high-quality conclusion paragraph generator, giving 1 paragraph based on the user's input. Focus on answering the paragraph's topic and apply the tone of voice. Make it human-written and easily readable.",
    params: [
      {
        value: 'length',
        analyticsValue: 'length',
        promptPart: {
          type: PromptPartType.template,
          template: 'Write a {{value}} paragraph',
        },
      },
      {
        value: 'paragraphGeneratorTopic',
        analyticsValue: 'paragraph_generator_topic',
        promptPart: {
          type: PromptPartType.template,
          template: ' about "{{value}}"',
        },
      },
      {
        value: 'tone',
        analyticsValue: 'tone',
        promptPart: {
          type: PromptPartType.template,
          template: ' with "{{value}} tone of voice"',
        },
      },
      {
        value: 'paragraphGeneratorDetails',
        analyticsValue: 'paragraph_generator_details',
        promptPart: {
          type: PromptPartType.template,
          template: ' and include the following details: "{{value}}".',
        },
      },
    ],
    model: GPT_VERSION,
    rephrase: true,
    hasOneResult: true,
  },
};

export const VALID_TONES = [
  'friendly',
  'luxury',
  'relaxed',
  'professional',
  'bold',
  'adventurous',
  'witty',
  'persuasive',
  'empathetic',
];
export const DEFAULT_TONE = 'friendly';

export const CATEGORY_ICON_PROPS = {
  width: 24,
  height: 24,
  background: 'transparent',
  fill: '#808080',
};

export const PRODUCT_CATEGORIES = [0, 6];
export const DESCRIPTION_CATEGORIES = [4, 14];
export const KEYWORDS_CATEGORIES = [10];

export const FORM_INITIAL_VALUES: IValues = {
  product: '',
  description: '',
  callToAction: '',
  tone: 'friendly',
  businessType: '',
  specialOffers: '',
  characterLimit: '',
  tellUsAboutYourself: '',
  textToParaphrase: '',
  language: 'english',
  wordToStart: '',
  numberOfResults: '10',
  keywords: '',
  purpose: '',
  adWriterRadioValue: 'body',
  describeYourImage: '',
  paragraphTitle: '',
  textToBeSummarised: '',
  format: 'short paragraph',
  textPurpose: '',
  describeText: '',
  describeScene: '',
  numberOfColors: '5',
  ideaGeneratorTopic: '',
  blogIdeaGeneratorTopic: '',
  bookIdeaGeneratorTopic: '',
  businessIdeaGeneratorTopic: '',
  contentIdeaGeneratorTopic: '',
  drawingIdeaGeneratorTopic: '',
  giftIdeaGeneratorTopic: '',
  paintingIdeaGeneratorTopic: '',
  sideHustleIdeaGeneratorTopic: '',
  startupIdeaGeneratorTopic: '',
  storyIdeaGeneratorTopic: '',
  articleWriterTopic: '',
  articleWriterDetails: '',
  seoCheckboxValue: true,
  length: 'medium',
  creativity: 'medium',
  describeStory: '',
  sloganPurpose: '',
  sloganWords: '',
  describeOutline: '',
  includeIdeas: '',
  outlinePurpose: '',
  describeTopic: '',
  metaTitleCheckbox: false,
  inputValue: '',
  dropdown01: '',
  dropdown02: 'English',
  essayInput: '',
  essayDetails: '',
  createInput: '',
  createDetails: '',
  titleGeneratorTopic: '',
  blogTitleGeneratorTopic: '',
  bookTitleGeneratorTopic: '',
  contentTitleGeneratorTopic: '',
  documentTitleGeneratorTopic: '',
  landingPageTitleGeneratorTopic: '',
  socialMediaPostTitleGeneratorTopic: '',
  storyTitleGeneratorTopic: '',
  videoTitleGeneratorTopic: '',
  videoScriptGeneratorTopic: '',
  videoScriptStyle: '',
  videoScriptLength: '',
  videoScriptComplexity: '',
  blogTitleGeneratorType: '',
  bookTitleGeneratorType: '',
  contentTitleGeneratorType: '',
  documentTitleGeneratorType: '',
  titleGeneratorType: '',
  landingPageTitleGeneratorType: '',
  socialMediaPostTitleGeneratorType: '',
  storyTitleGeneratorType: '',
  videoTitleGeneratorType: '',
  promptGeneratorTopic: '',
  promptGeneratorDetails: '',
  textPromptGeneratorTopic: '',
  textPromptGeneratorDetails: '',
  imagePromptGeneratorTopic: '',
  imagePromptGeneratorDetails: '',
  videoPromptGeneratorTopic: '',
  videoPromptGeneratorDetails: '',
  codePromptGeneratorTopic: '',
  codePromptGeneratorDetails: '',
  paragraphGeneratorTopic: '',
  paragraphGeneratorDetails: '',
  keywordsGeneratorTopic: '',
  keywordsGeneratorNumber: '',
};

export const TONE_VALUES = [
  {
    title: '😊 Friendly',
    value: 'friendly',
  },
  {
    title: '💎 Luxury',
    value: 'luxury',
  },
  {
    title: '😌 Relaxed',
    value: 'relaxed',
  },
  {
    title: '💼 Professional',
    value: 'professional',
  },
  {
    title: '💪 Bold',
    value: 'bold',
  },
  {
    title: '⛺ Adventurous',
    value: 'adventurous',
  },
  {
    title: '💡 Witty',
    value: 'witty',
  },
  {
    title: '🧠 Persuasive',
    value: 'persuasive',
  },
  {
    title: '😍 Empathetic',
    value: 'empathetic',
  },
];

export const LANGUAGE_VALUES = [
  {
    title: 'English/(US)',
    value: 'english',
  },
  {
    title: 'Simplified Chinese/中文(简体)',
    value: 'chinese',
  },
  {
    title: 'German/Deutsch',
    value: 'german',
  },
  {
    title: 'French/Français',
    value: 'french',
  },
  {
    title: 'Italian/Italiano',
    value: 'italian',
  },
  {
    title: 'India/Hindi',
    value: 'hindi',
  },
  {
    title: 'Japanese/日本',
    value: 'japanese',
  },
  {
    title: 'Korean/한국어',
    value: 'korean',
  },
  {
    title: 'Russian/Русский',
    value: 'russian',
  },
  {
    title: 'Spanish/Español',
    value: 'spanish',
  },
];

export const LANGUAGES_WITHOUT_WORD_COUNT = ['chinese', 'hindi', 'japanese', 'korean', 'russian'];

export const MAX_CHRS = 1000;
export const MIN_CHRS = 100;
export const TEXT_ROWS = 3;
export const MAX_NO_OF_RESULTS = 60;
export const MINUTES_FOR_MAX_NO_OF_RESULTS = 60;
export const LOCAL_STORAGE_RESULTS_KEY = 'qt_no_of_results';
export const LOCAL_STORAGE_SESSION_KEY = 'qt_session_id';
export const A_B_EXPERIMENT_ID = 'IWOo9n7bSJS-hp01rxjhvw';
export const ADS_GENERATOR_EXPERIMENT_ID = 't1L82hqST3qQ0qJ5yep2PA';
export const BIO_GENERATOR_EXPERIMENT_ID = 'WZnbaHaMR_-hjV9cLrIOqQ';

export const GENERAL_LANDING_CARD_SECTION_IMGS = [
  {
    src: 'https://cdn140.picsart.com/68721397183638063211.webp',
  },
  {
    src: 'https://cdn140.picsart.com/38242970981570235865.webp',
  },
  {
    src: 'https://cdn140.picsart.com/30068252809047449629.webp',
  },
  {
    src: 'https://cdn140.picsart.com/94188132819015905082.webp',
  },
];

export const quicktoolsLogoSrc =
  'https://pastatic.picsart.com/cms-pastatic/227d1042-e9d3-4f4d-b720-0e1505dfd52c.webp';

const GEN_TEXT_MENU_CATEGORY: MenuCategory = {
  type: MenuCategoryType.Generate,
  icon: IconSketch,
  text: 'Generate text',
  subcategories: [
    { category: ARTICLE_WRITER_CATEGORY, text: 'Article', icon: IconDetails },
    { category: EMAIL_WRITER_CATEGORY, text: 'Email', icon: IconEmail },
    { category: ESSAY_WRITER_CATEGORY, text: 'Essay', icon: IconBookmark },
    { category: IDEA_GENERATOR_CATEGORY, text: 'Ideas', icon: IconLearn },
    {
      category: KEYWORD_GENERATOR_CATEGORY,
      text: 'Keywords',
      icon: IconSubtitleSquare,
    },
    {
      category: BUSINESS_NAME_CATEGORY,
      text: 'Names',
      icon: IconProfileOutline,
    },
    { category: PAGE_OUTLINE_CATEGORY, text: 'Outline', icon: IconTemplate },
    {
      category: PARAGRAPH_GENERATOR_CATEGORY,
      text: 'Paragraph',
      icon: IconSpacingVertical,
    },
    {
      category: PROMPT_GENERATOR_CATEGORY,
      text: 'Prompt',
      icon: IconDevelop,
    },
    {
      category: VIDEO_SCRIPT_GENERATOR_CATEGORY,
      text: 'Script',
      icon: IconTextToVideo,
    },
    { category: STORY_GENERATOR_CATEGORY, text: 'Story', icon: IconBookmark },
    {
      category: TITLE_GENERATOR_CATEGORY,
      text: 'Titles',
      icon: IconEmailHeader,
    },
    { category: TRANSLATOR_CATEGORY, text: 'Translations', icon: IconCallout },
  ],
};

const EDIT_TEXT_MENU_CATEGORY: MenuCategory = {
  type: MenuCategoryType.Edit,
  icon: IconBlankWrite,
  text: 'Edit text',
  subcategories: [
    {
      category: EXPAND_TEXT_CATEGORY,
      text: 'Expand',
      icon: IconUnfold,
    },
    {
      category: GRAMMAR_CHECKER_CATEGORY,
      text: 'Grammar checker',
      icon: IconAssignmentTurnedIn,
    },
    {
      category: REPHRASER_CATEGORY,
      text: 'Paraphraser',
      icon: IconRefresh,
    },
    {
      category: SHORTEN_TEXT_CATEGORY,
      text: 'Shorten',
      icon: IconFold,
    },
    {
      category: SUMMARIZER_CATEGORY,
      text: 'Summarizer',
      icon: IconPoster,
    },
  ],
};

const CONTENT_MENU_CATEGORY: MenuCategory = {
  type: MenuCategoryType.Content,
  icon: IconDetails,
  text: 'Content',
  subcategories: [
    { category: ARTICLE_WRITER_CATEGORY, text: 'Article', icon: IconDetails },
    {
      category: BLOG_TITLE_GENERATOR_CATEGORY,
      text: 'Blog titles',
      icon: IconLearn,
    },
    {
      category: INSTAGRAM_CAPTION_CATEGORY,
      text: 'Captions',
      icon: IconSubtitle,
    },
    {
      category: INSTAGRAM_HASHTAG_CATEGORY,
      text: 'Hashtags',
      icon: IconHashtag,
    },
    {
      category: CONTENT_IDEA_GENERATOR_CATEGORY,
      text: 'Ideas',
      icon: IconLearn,
    },
    {
      category: KEYWORD_GENERATOR_CATEGORY,
      text: 'Keywords',
      icon: IconSubtitleSquare,
    },
    {
      category: META_DATA_MAKER_CATEGORY,
      text: 'Meta description',
      icon: IconEmbedCode,
    },
    { category: PAGE_OUTLINE_CATEGORY, text: 'Outline', icon: IconTemplate },
    {
      category: PARAGRAPH_GENERATOR_CATEGORY,
      text: 'Paragraph',
      icon: IconSpacingVertical,
    },
    {
      category: VIDEO_SCRIPT_GENERATOR_CATEGORY,
      text: 'Script',
      icon: IconTextToVideo,
    },
    {
      category: SOCIAL_MEDIA_BIO_CATEGORY,
      text: 'Social media bio',
      icon: IconFaceMask,
    },
    {
      category: STORY_IDEA_GENERATOR_CATEGORY,
      text: 'Story ideas',
      icon: IconLearn,
    },
    {
      category: CONTENT_TITLE_GENERATOR_CATEGORY,
      text: 'Titles',
      icon: IconEmailHeader,
    },
    {
      category: VIDEO_TITLE_GENERATOR_CATEGORY,
      text: 'Video titles',
      icon: IconEmailHeader,
    },
  ],
};

const BUSINESS_MENU_CATEGORY: MenuCategory = {
  type: MenuCategoryType.Business,
  icon: IconJob,
  text: 'Business',
  subcategories: [
    {
      category: BUSINESS_NAME_CATEGORY,
      text: 'Business names',
      icon: IconJob,
    },
    {
      category: EMAIL_WRITER_CATEGORY,
      text: 'Email writer',
      icon: IconEmail,
    },
    {
      category: BUSINESS_IDEA_GENERATOR_CATEGORY,
      text: 'Ideas',
      icon: IconLearn,
    },
    {
      category: SIDE_HUSTLE_IDEA_GENERATOR_CATEGORY,
      text: 'Side hustle ideas',
      icon: IconLearn,
    },
    {
      category: MARKETING_SLOGAN_CATEGORY,
      text: 'Slogan',
      icon: IconFeedback,
    },
    {
      category: STARTUP_IDEA_GENERATOR_CATEGORY,
      text: 'Startup ideas',
      icon: IconLearn,
    },
  ],
};

const MARKETING_MENU_CATEGORY: MenuCategory = {
  type: MenuCategoryType.Marketing,
  icon: IconPromote,
  text: 'Marketing',
  subcategories: [
    {
      category: AD_WRITER_CATEGORY,
      text: 'Ad copy',
      icon: IconPoster,
    },
    { category: ARTICLE_WRITER_CATEGORY, text: 'Article', icon: IconDetails },
    {
      category: BLOG_TITLE_GENERATOR_CATEGORY,
      text: 'Blog titles',
      icon: IconEmailHeader,
    },
    { category: EMAIL_WRITER_CATEGORY, text: 'Email', icon: IconEmail },
    {
      category: KEYWORD_GENERATOR_CATEGORY,
      text: 'Keywords',
      icon: IconSubtitleSquare,
    },
    {
      category: LANDING_PAGE_TITLE_GENERATOR_CATEGORY,
      text: 'Landing page titles',
      icon: IconEmailHeader,
    },
    {
      category: META_DATA_MAKER_CATEGORY,
      text: 'Meta description',
      icon: IconEmbedCode,
    },
    {
      category: PARAGRAPH_GENERATOR_CATEGORY,
      text: 'Paragraph',
      icon: IconSpacingVertical,
    },
    {
      category: PRODUCT_DESCRIPTION_CATEGORY,
      text: 'Product description',
      icon: IconTemplates,
    },
    {
      category: MARKETING_SLOGAN_CATEGORY,
      text: 'Slogan',
      icon: IconFeedback,
    },
  ],
};

export const MENU_CATEGORIES: MenuCategory[] = [
  GEN_TEXT_MENU_CATEGORY,
  EDIT_TEXT_MENU_CATEGORY,
  CONTENT_MENU_CATEGORY,
  BUSINESS_MENU_CATEGORY,
  MARKETING_MENU_CATEGORY,
];

export const createPath = '/text/ai-writer/create/';

export const MAIN_CONTENT_ID = 'main-content';
